import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@opendoor/atoms/utils';
import { MOBILE_MINI_PDP_PANEL_HEIGHT } from 'components/marketplace/map/types';
import { type GestureAxis, resolveGestureAxis } from 'components/marketplace/shared/gestureAxis';
import {
  MOBILE_MINI_PDP_TRANSITION_MS,
  PANEL_EASING,
} from 'components/marketplace/shared/panelTransitions';
import { shouldDismissVerticalSwipe } from 'components/marketplace/shared/swipeDismiss';

type ResolvedGestureAxis = GestureAxis | null;
type GesturePhase = 'idle' | 'dragging' | 'animating';

const MINI_PDP_SCROLL_REGION_SELECTOR = '[data-mini-pdp-scroll]';
const CAROUSEL_PHOTO_TAP_SELECTOR = '[data-carousel-photo-tap]';

const getScrollRegion = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof Element)) {
    return null;
  }
  return target.closest<HTMLElement>(MINI_PDP_SCROLL_REGION_SELECTOR);
};

const isInteractiveTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) {
    return false;
  }
  const interactive = target.closest('button, a, [role="button"]');
  if (!interactive) {
    return false;
  }
  // Full-bleed photo tap targets render as buttons but share the carousel
  // swipe surface — vertical swipes there should dismiss, not be blocked.
  if (interactive.closest(CAROUSEL_PHOTO_TAP_SELECTOR)) {
    return false;
  }
  return true;
};

const shouldIgnoreTouchStart = (target: EventTarget | null): boolean => {
  if (isInteractiveTarget(target)) {
    return true;
  }
  const scrollRegion = getScrollRegion(target);
  if (scrollRegion && scrollRegion.scrollTop > 0) {
    return true;
  }
  return false;
};

const setPanelOffsetY = (panel: HTMLElement, offsetY: string) => {
  panel.style.transform = `translateY(${offsetY})`;
};

export function useMobileMiniPDPSwipeDismiss(onClose: () => void, isActive: boolean) {
  const panelRef = useRef<HTMLElement>(null);
  const startYRef = useRef(0);
  const startXRef = useRef(0);
  const gestureAxisRef = useRef<ResolvedGestureAxis>(null);
  const gesturePhaseRef = useRef<GesturePhase>('idle');
  const onCloseRef = useRef(onClose);
  const [gesturePhase, setGesturePhase] = useState<GesturePhase>('idle');

  // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
  onCloseRef.current = onClose;

  const setPhase = useCallback((phase: GesturePhase) => {
    gesturePhaseRef.current = phase;
    setGesturePhase(phase);
  }, []);

  const clearPanelMotion = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }
    panel.style.transition = '';
    panel.style.transform = '';
    panel.style.translate = '';
  }, []);

  const resetGesture = useCallback(() => {
    clearPanelMotion();
    setPhase('idle');
    gestureAxisRef.current = null;
  }, [clearPanelMotion, setPhase]);

  const animatePanelTo = useCallback(
    (offsetY: string, onComplete: () => void) => {
      const panel = panelRef.current;
      if (!panel) {
        onComplete();
        return;
      }

      setPhase('animating');

      const handleTransitionEnd = (event: TransitionEvent) => {
        if (event.target !== panel || event.propertyName !== 'transform') {
          return;
        }
        panel.removeEventListener('transitionend', handleTransitionEnd);
        onComplete();
      };

      panel.addEventListener('transitionend', handleTransitionEnd);
      panel.style.transition = `transform ${MOBILE_MINI_PDP_TRANSITION_MS}ms ${PANEL_EASING}`;
      setPanelOffsetY(panel, offsetY);
    },
    [setPhase],
  );

  useLayoutEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const panel = panelRef.current;
    if (!panel) {
      return undefined;
    }

    const handleTouchStart = (event: TouchEvent) => {
      if (shouldIgnoreTouchStart(event.target)) {
        return;
      }

      const touch = event.touches[0];
      startYRef.current = touch.clientY;
      startXRef.current = touch.clientX;
      gestureAxisRef.current = null;
      clearPanelMotion();
      setPhase('dragging');
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (gesturePhaseRef.current !== 'dragging') {
        return;
      }

      const touch = event.touches[0];
      const dx = touch.clientX - startXRef.current;
      const dy = touch.clientY - startYRef.current;

      if (gestureAxisRef.current === null) {
        const axis = resolveGestureAxis(dx, dy);
        if (axis === null) {
          return;
        }
        gestureAxisRef.current = axis;
      }

      if (gestureAxisRef.current === 'horizontal') {
        return;
      }

      if (dy > 0) {
        event.preventDefault();
      }
      panel.style.transition = 'none';
      setPanelOffsetY(panel, `${Math.max(0, dy)}px`);
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (gesturePhaseRef.current !== 'dragging') {
        return;
      }

      if (gestureAxisRef.current !== 'vertical') {
        resetGesture();
        return;
      }

      const touch = event.changedTouches[0];
      const dx = touch.clientX - startXRef.current;
      const dy = touch.clientY - startYRef.current;

      if (shouldDismissVerticalSwipe(dx, dy)) {
        animatePanelTo(MOBILE_MINI_PDP_PANEL_HEIGHT, () => {
          // Hold the dismissed position without inline transition so React
          // can apply the closed panel classes without a visible snap-back.
          panel.style.transition = 'none';
          onCloseRef.current();
        });
        return;
      }

      if (dy > 0) {
        animatePanelTo('0px', resetGesture);
        return;
      }

      resetGesture();
    };

    panel.addEventListener('touchstart', handleTouchStart, { capture: true, passive: true });
    panel.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false });
    panel.addEventListener('touchend', handleTouchEnd, { capture: true, passive: true });
    panel.addEventListener('touchcancel', handleTouchEnd, { capture: true, passive: true });

    return () => {
      panel.removeEventListener('touchstart', handleTouchStart, { capture: true });
      panel.removeEventListener('touchmove', handleTouchMove, { capture: true });
      panel.removeEventListener('touchend', handleTouchEnd, { capture: true });
      panel.removeEventListener('touchcancel', handleTouchEnd, { capture: true });
    };
  }, [animatePanelTo, clearPanelMotion, isActive, resetGesture, setPhase]);

  // Clear imperative drag styles once the panel unmounts/closes so the next
  // open starts from a clean translate state.
  useEffect(() => {
    if (isActive) {
      return;
    }
    const panel = panelRef.current;
    if (panel) {
      panel.style.transition = 'none';
      panel.style.transform = '';
      panel.style.translate = '';
    }
    gestureAxisRef.current = null;
    gesturePhaseRef.current = 'idle';
    // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
    setGesturePhase('idle');
  }, [isActive]);

  const panelClassName = cn(
    (gesturePhase === 'dragging' || gesturePhase === 'animating') && 'transition-none',
  );

  return {
    panelRef,
    panelClassName,
  };
}
